// frontend/src/pages/manufacturing/v2/MaterialShortagesV2.jsx
//
// "What are we short of?" — every item open v2 orders still need more of
// than free stock plus planned production covers. Backed by
// GET /api/v2/shortages/, which works from the movement ledger rather than
// the cached reserved figure, so it shows the true position.
//
//   make — a manufactured sub-assembly with no (or not enough) work orders
//   buy  — a component that has to be purchased / booked in
//
// The whole report is one small payload, so search and the make/buy filter
// are client-side. Expand a row to see which orders are waiting on the item.
//
// "Create missing work orders" raises a sub-assembly WO inside each order
// left short of a made-in-house item (POST .../create-work-orders/). It
// always previews first (dry_run) and only writes after confirmation.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  LinearProgress, Alert, Link, Collapse, IconButton, InputAdornment,
  ToggleButton, ToggleButtonGroup, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import {
  Refresh, Search, ReportProblem, KeyboardArrowDown, KeyboardArrowUp, Build,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { v2 } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';

// Round to 3 dp for display — raw floats show artifacts like 7.260000000000001.
const fmtQty = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return String(Math.round(n * 1000) / 1000);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB');
};

const ACTION_LABELS = { make: 'Needs work order', buy: 'Needs stock' };
const ACTION_COLORS = { make: 'error', buy: 'warning' };

const SummaryCard = ({ label, value, color, testId }) => (
  <Grid item xs={6} md={3}>
    <Card>
      <CardContent>
        <Typography variant="h4" color={color} data-testid={testId}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  </Grid>
);

const ShortageRow = ({ row, onOpenOrder }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover data-testid={`shortage-row-${row.item_code}`}>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Hide orders' : 'Show orders'}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          {row.item_code}
        </TableCell>
        <TableCell>{row.item_name}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={ACTION_LABELS[row.action] || row.action}
            color={ACTION_COLORS[row.action] || 'default'}
          />
        </TableCell>
        <TableCell align="right">{fmtQty(row.on_hand)}</TableCell>
        <TableCell align="right">{fmtQty(row.allocated_to_orders)}</TableCell>
        <TableCell align="right">{fmtQty(row.required)}</TableCell>
        <TableCell align="right">{fmtQty(row.incoming)}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>
            {fmtQty(row.shortage)}
          </Typography>
        </TableCell>
        <TableCell align="right">{row.order_count}</TableCell>
        <TableCell align="right">
          {row.reserved_mismatch ? (
            <Tooltip
              title={`The system's reserved figure is ${fmtQty(row.system_reserved)} but open orders actually need ${fmtQty(row.required)}.`}
            >
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={fmtQty(row.system_reserved)}
              />
            </Tooltip>
          ) : fmtQty(row.system_reserved)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Orders waiting on {row.item_code}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Work order</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Still needs</TableCell>
                    <Tooltip title="What this order is left without once free stock has gone to the earliest-due orders">
                      <TableCell align="right">Not covered</TableCell>
                    </Tooltip>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.orders.map((o) => (
                    <TableRow key={o.tree_id}>
                      <TableCell>
                        {o.order_id ? (
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => onOpenOrder(o.order_id)}
                          >
                            {o.order_number}
                          </Link>
                        ) : 'Stock build'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{o.wo_number || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{o.product_code || '—'}</TableCell>
                      <TableCell>{fmtDate(o.due_date)}</TableCell>
                      <TableCell align="right">{fmtQty(o.quantity)}</TableCell>
                      <TableCell
                        align="right"
                        sx={Number(o.uncovered) > 0 ? { color: 'error.main', fontWeight: 700 } : undefined}
                      >
                        {fmtQty(o.uncovered)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const MaterialShortagesV2 = () => {
  usePageTitle('Material Shortages V2');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('all');
  // Create-work-orders flow: preview (dry run) → confirm → create.
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await v2.shortages.get();
      setData(res.data);
    } catch (e) {
      setError(true);
      toast.error('Failed to load material shortages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPreview = async () => {
    setWorking(true);
    try {
      const res = await v2.shortages.createWorkOrders({ dry_run: true });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not work out the missing work orders');
    } finally {
      setWorking(false);
    }
  };

  const confirmCreate = async () => {
    setWorking(true);
    try {
      const res = await v2.shortages.createWorkOrders({});
      const { work_orders_created: created, work_orders_raised: raised } = res.data;
      toast.success(
        `Created ${created} work order${created === 1 ? '' : 's'}`
        + (raised ? ` and increased ${raised}` : ''),
      );
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to create work orders — nothing was changed');
    } finally {
      setWorking(false);
    }
  };

  const previewCount = preview
    ? preview.work_orders_created + preview.work_orders_raised
    : 0;

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.items || []).filter((r) => {
      if (action !== 'all' && r.action !== action) return false;
      if (!term) return true;
      return (
        r.item_code.toLowerCase().includes(term)
        || (r.item_name || '').toLowerCase().includes(term)
        || r.orders.some((o) => (o.order_number || '').toLowerCase().includes(term))
      );
    });
  }, [data, search, action]);

  const summary = data?.summary;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportProblem color="primary" />
              Material Shortages
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<Build />}
              onClick={openPreview}
              disabled={loading || working || !summary?.make_short}
              size="small"
              data-testid="create-work-orders"
            >
              Create missing work orders
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={load}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Items that open V2 orders still need more of than there is free
            stock and planned production to cover. Short = Required − (On hand
            − Allocated) − Being made. &quot;Needs work order&quot; items are
            made in-house but have no work order for the shortfall.
          </Typography>
        </CardContent>
      </Card>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <SummaryCard
            label="Items short"
            value={summary.items_short}
            color={summary.items_short ? 'error' : 'success.main'}
            testId="summary-items-short"
          />
          <SummaryCard
            label="Need a work order"
            value={summary.make_short}
            color="error"
            testId="summary-make-short"
          />
          <SummaryCard
            label="Need stock"
            value={summary.buy_short}
            color="warning.main"
            testId="summary-buy-short"
          />
          <SummaryCard
            label="Orders affected"
            value={summary.orders_affected}
            color="text.primary"
            testId="summary-orders-affected"
          />
        </Grid>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Search"
                placeholder="Item code, name or order number"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                  ),
                }}
                inputProps={{ 'data-testid': 'filter-search' }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={action}
                onChange={(e, value) => value && setAction(value)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="make" data-testid="filter-make">Needs work order</ToggleButton>
                <ToggleButton value="buy" data-testid="filter-buy">Needs stock</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && <LinearProgress data-testid="loading-state" />}
          {error && !loading && (
            <Alert severity="error" sx={{ my: 2 }} data-testid="error-state">
              Failed to load material shortages.
              <Button onClick={load} size="small" sx={{ ml: 1 }}>Retry</Button>
            </Alert>
          )}
          {!loading && !error && rows.length === 0 && (
            <Alert
              severity={data?.items?.length ? 'info' : 'success'}
              sx={{ my: 2 }}
              data-testid="empty-state"
            >
              {data?.items?.length
                ? 'No shortages match your filters.'
                : 'Nothing is short — every open order is covered by stock or planned production.'}
            </Alert>
          )}
          {!loading && !error && rows.length > 0 && (
            <TableContainer>
              <Table size="small" data-testid="shortages-table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Item</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell align="right">On hand</TableCell>
                    <TableCell align="right">Allocated</TableCell>
                    <TableCell align="right">Required</TableCell>
                    <TableCell align="right">Being made</TableCell>
                    <TableCell align="right">Short</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">System reserved</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <ShortageRow
                      key={row.item_id}
                      row={row}
                      onOpenOrder={(id) => navigate(`/manufacturing/customer-orders/${id}`)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!preview}
        onClose={() => !working && setPreview(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create missing work orders</DialogTitle>
        <DialogContent dividers data-testid="create-preview">
          {previewCount === 0 ? (
            <Alert severity="info">
              Nothing to create — no order is short of an item made in-house.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This will create <strong>{preview?.work_orders_created}</strong> new
                work order{preview?.work_orders_created === 1 ? '' : 's'}
                {preview?.work_orders_raised ? (
                  <>
                    {' '}and increase <strong>{preview.work_orders_raised}</strong> existing
                    one{preview.work_orders_raised === 1 ? '' : 's'}
                  </>
                ) : null}
                {' '}across <strong>{preview?.orders_affected}</strong> order
                {preview?.orders_affected === 1 ? '' : 's'}. Each one is added to
                the order that is short, for exactly the quantity that order
                is missing. Free stock goes to the earliest-due orders first.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Work orders</TableCell>
                    <TableCell align="right">Total qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(preview?.items || []).map((it) => (
                    <TableRow key={it.item_code}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{it.item_code}</TableCell>
                      <TableCell>{it.item_name}</TableCell>
                      <TableCell align="right">{it.work_orders}</TableCell>
                      <TableCell align="right">{fmtQty(it.quantity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} disabled={working}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmCreate}
            disabled={working || previewCount === 0}
            data-testid="confirm-create-work-orders"
          >
            {working ? 'Creating…' : `Create ${previewCount} work order${previewCount === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialShortagesV2;
